import { useState, useEffect, useRef } from 'react';
import {
    loadMarketProbability,
    type MarketSeriesData,
} from './marketPricesClient';

export interface MultiMarketProbResult {
    series: MarketSeriesData[];
    loading: boolean; // true while no lead market has loaded yet
    allLoaded: boolean; // true when every market has resolved
    error: Error | null;
    /**
     * Every market of this set has answered (or failed). Unlike `allLoaded`,
     * false from the first render with a new set, before its requests have
     * started; a refetch of the same set for another window keeps it.
     */
    settled: boolean;
}

export function useMultiMarketProbability(
    conditionIds: string[],
    startDate: Date | null,
    endDate: Date | null,
    intervalMinutes = 1440,
    leadIds?: Set<string>
): MultiMarketProbResult {
    const [series, setSeries] = useState<MarketSeriesData[]>([]);
    const [loading, setLoading] = useState(false);
    const [allLoaded, setAllLoaded] = useState(true);
    const [error, setError] = useState<Error | null>(null);
    const [settledKey, setSettledKey] = useState<string | null>(null);
    const callIdRef = useRef(0);
    // Tracks the market set last fetched, so a "different markets" load (reset +
    // show skeleton) is distinguished from a same-markets window refetch (keep
    // the current data on screen so zooming doesn't flash the skeleton).
    const prevMarketsKeyRef = useRef<string | null>(null);

    const idsKey = conditionIds.join(',');
    const leadKey = leadIds ? [...leadIds].sort().join(',') : '';

    useEffect(() => {
        if (conditionIds.length === 0 || !startDate || !endDate) {
            setSeries([]);
            setLoading(false);
            setAllLoaded(true);
            return;
        }

        const callId = ++callIdRef.current;
        const controller = new AbortController();

        // A window-only refetch keeps the same markets — keep the current data
        // visible (no skeleton) and swap it in as the new window resolves. Only
        // a market-set change resets to the loading skeleton.
        const marketsKey = `${idsKey}|${leadKey}`;
        const marketsChanged = prevMarketsKeyRef.current !== marketsKey;
        prevMarketsKeyRef.current = marketsKey;

        setAllLoaded(false);
        setError(null);
        if (marketsChanged) {
            setLoading(true);
            setSeries([]);
            // Not settled by an earlier answer for this set (left and come
            // back to before the set in between answered).
            setSettledKey(null);
        }

        const isLead = leadIds
            ? (cid: string) => leadIds.has(cid)
            : (cid: string) => cid === conditionIds[0];
        const pendingCount = { current: conditionIds.length };
        // Accumulate this fetch's results into a fresh list and replace the
        // visible series as they arrive — so stale data is swapped, not cleared.
        const collected: MarketSeriesData[] = [];

        conditionIds.forEach(cid => {
            loadMarketProbability(cid, startDate, endDate, intervalMinutes, {
                signal: controller.signal,
            })
                .then(points => {
                    if (callId !== callIdRef.current) return;
                    collected.push({ conditionId: cid, data: points });
                    setSeries([...collected]);
                    if (isLead(cid)) setLoading(false);
                })
                .catch(err => {
                    if (callId !== callIdRef.current) return;
                    if (err?.name === 'AbortError') return;
                    console.error(`market-probability failed for ${cid}:`, err);
                    if (isLead(cid)) {
                        setError(err);
                        setLoading(false);
                    }
                })
                .finally(() => {
                    if (callId !== callIdRef.current) return;
                    pendingCount.current -= 1;
                    if (pendingCount.current === 0) {
                        setAllLoaded(true);
                        setSettledKey(marketsKey);
                    }
                });
        });

        return () => controller.abort();
    }, [
        idsKey,
        leadKey,
        startDate?.getTime(),
        endDate?.getTime(),
        intervalMinutes,
    ]);

    const settled =
        conditionIds.length === 0 ||
        !startDate ||
        !endDate ||
        settledKey === `${idsKey}|${leadKey}`;
    return { series, loading, allLoaded, error, settled };
}
